import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraHeading, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { useView } from '../../../lib/access/useView';
import { projectDataClient } from '../../../lib/connect/clients';
import { proposalHref } from '../../../lib/proposalHref';
/** Only receiving authority is resolved here; Proposal membership is never consulted. */ export function SubmittedHandoffs({ preAwardProjectId }) {
    const { user } = useAuth();
    return /*#__PURE__*/ _jsx(ReceivingHandoffs, {
        preAwardProjectId: preAwardProjectId
    }, JSON.stringify([
        user?.tenant_id,
        user?.id,
        preAwardProjectId
    ]));
}
function ReceivingHandoffs({ preAwardProjectId }) {
    const { user } = useAuth();
    const view = useView({
        kind: 'pre_award_project',
        id: preAwardProjectId,
        objectType: 'delivery.programs',
        objectId: preAwardProjectId
    }, {
        action: 'read',
        enabled: !!preAwardProjectId
    });
    const allowed = !!user?.tenant_id && !view.isLoading && view.viewMode !== 'no_view' && view.can('read');
    const query = useInfiniteQuery({
        queryKey: [
            'pre-award-submitted-handoffs',
            user?.tenant_id,
            user?.id,
            preAwardProjectId
        ],
        initialPageParam: 0,
        queryFn: ({ pageParam })=>projectDataClient.getBidBaseline({
                preAwardProjectId,
                historyOffset: pageParam,
                historyLimit: 20
            }),
        getNextPageParam: (page, pages)=>{
            const loaded = pages.reduce((count, entry)=>count + entry.handoffHistory.length, 0);
            return page.handoffHistory.length > 0 && BigInt(loaded) < page.historyTotal ? loaded : undefined;
        },
        enabled: allowed,
        retry: false,
        gcTime: 0
    });
    if (view.isLoading) return /*#__PURE__*/ _jsx("output", {
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Checking receiving access…"
        })
    });
    if (!allowed) return /*#__PURE__*/ _jsx(SpecraText, {
        role: "alert",
        children: "Receiving access required. This workspace uses its own Programs permissions; sending a Proposal grants no receiving membership or approval authority."
    });
    if (query.isError) {
        const denied = query.error instanceof ConnectError && [
            Code.PermissionDenied,
            Code.Unauthenticated
        ].includes(query.error.code);
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    role: "alert",
                    children: query.error instanceof ConnectError && query.error.code === Code.NotFound ? 'Receiving workspace unavailable or access required. Check the link or ask a Programs administrator to confirm your receiving access. Proposal access does not grant receiving permissions.' : denied ? 'Receiving access required. Programs permission on this receiving workspace is independent of Proposal access.' : query.error.message
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "outline",
                    onClick: ()=>void query.refetch(),
                    children: "Retry handoff history"
                })
            ]
        });
    }
    if (query.isPending) return /*#__PURE__*/ _jsx("output", {
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Loading frozen submitted handoffs…"
        })
    });
    // The workspace's original baseline remains authoritative for comparison. New
    // descendants are additional immutable handoffs, never replacements for it.
    const baseline = query.data?.pages[0]?.baseline;
    const history = query.data?.pages.flatMap((page)=>page.handoffHistory) ?? [];
    const rows = [
        ...baseline ? [
            baseline
        ] : [],
        ...history
    ].filter((row, index, all)=>row.preAwardProjectId === preAwardProjectId && all.findIndex((entry)=>entry.id === row.id) === index);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        "data-testid": Testids.proposalHandoff.receivingPanel,
        "data-pre-award-id": preAwardProjectId,
        children: [
            /*#__PURE__*/ _jsx(SpecraHeading, {
                size: "sm",
                children: "Submitted bid handoffs · retained history"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                className: "block",
                children: "Read-only snapshots captured at handoff. Later Proposal edits do not change these documents or commitments. Receiving permissions do not confer Program Lead identity or PM approval authority."
            }),
            query.isFetching && /*#__PURE__*/ _jsx("output", {
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Refreshing retained history…"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                disabled: query.isFetching,
                onClick: ()=>void query.refetch(),
                children: "Refresh handoff history"
            }),
            !rows.length && /*#__PURE__*/ _jsx(SpecraText, {
                children: "No frozen handoff is available."
            }),
            rows.map((row)=>/*#__PURE__*/ _jsxs("article", {
                    className: "space-y-4",
                    "data-testid": Testids.proposalHandoff.historyEntry,
                    "data-handoff-id": row.id,
                    "data-submission-id": row.submissionId,
                    "data-baseline-id": row.id,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCard, {
                            children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
                                className: "space-y-2 break-words p-4",
                                children: [
                                    /*#__PURE__*/ _jsxs(SpecraHeading, {
                                        size: "sm",
                                        children: [
                                            "Handoff ",
                                            row.id
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "neutral",
                                        children: row.id === baseline?.id ? 'Workspace comparison baseline' : 'Retained descendant handoff'
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Proposal: ",
                                            row.proposalProjectId,
                                            " · Submission:",
                                            ' ',
                                            row.submissionId || 'Legacy baseline — no recorded submission ID'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        className: specraButtonVariants({
                                            variant: 'outline'
                                        }),
                                        "data-testid": Testids.proposalHandoff.sourceSubmission,
                                        to: proposalHref(row.proposalProjectId, `home${row.submissionId ? `?submission=${encodeURIComponent(row.submissionId)}` : ''}`),
                                        children: row.submissionId ? `Source Proposal · submission ${row.submissionId}` : 'Source Proposal'
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Handoff actor/time: ",
                                            row.frozenBy || 'Not captured',
                                            " ·",
                                            ' ',
                                            row.frozenAt || 'Not captured',
                                            " · Version ",
                                            row.version
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Pricing version: ",
                                            row.pricingVersionId || 'Not captured'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Staffing version: ",
                                            row.staffingVersionId || 'Not captured'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Solicitation version: ",
                                            row.solicitationVersionId || 'Not captured'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        className: "block",
                                        children: [
                                            "Source document IDs: ",
                                            row.sourceArtifactIds.join(', ') || 'Not captured'
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Selected submission snapshot and provenance",
                            json: row.submissionSnapshotJson,
                            testId: Testids.proposalHandoff.snapshot
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Pricing commitments",
                            json: row.pricingBaselineJson
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Staffing commitments",
                            json: row.staffingPlanJson
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Technical commitments",
                            json: row.technicalCommitmentsJson
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Planned deliverables",
                            json: row.plannedDeliverablesJson
                        }),
                        /*#__PURE__*/ _jsx(FrozenSection, {
                            title: "Assumptions",
                            json: row.assumptionsJson
                        })
                    ]
                }, row.id)),
            query.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                "data-testid": Testids.proposalHandoff.loadMore,
                disabled: query.isFetching,
                onClick: ()=>void query.fetchNextPage(),
                children: query.isFetchingNextPage ? 'Loading more handoffs…' : 'Load more handoffs'
            })
        ]
    });
}
function FrozenSection({ title, json, testId }) {
    let display = json || 'Not captured in this handoff.';
    try {
        if (json) display = JSON.stringify(JSON.parse(json), null, 2);
    } catch  {
    /* Preserve a malformed frozen value verbatim. */ }
    return /*#__PURE__*/ _jsx(SpecraCard, {
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-2 p-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: title
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    "data-testid": testId,
                    className: "block whitespace-pre-wrap break-words",
                    children: display
                })
            ]
        })
    });
}
